import React from "react";

// Common cell props configuration
export const getCommonCellProps = (minWidth = "fit-content", padding = "8px") => ({
    style: {
        minWidth: minWidth,
        padding: padding,
        paddingLeft: "17px",
    },
});

// Common header props configuration
export const getCommonHeaderProps = (minWidth = "fit-content", padding = "0px") => ({
    style: {
        minWidth: minWidth,
        fontWeight: "bold",
        paddingLeft: padding,
    },
});

// Create a clickable customer name cell
export const createCustomerNameColumn = (tableData, onCustomerClick, minWidth = "fit-content") => ({
    name: "customer_name",
    label: "Customer",
    options: {
        customBodyRenderLite: (dataIndex) => {
            const item = tableData[dataIndex];
            return (
                <a
                    rel="noreferrer"
                    style={{ cursor: "pointer" }}
                    onClick={() => onCustomerClick(item)}
                >
                    {item.customer_name}
                </a>
            );
        },
        setCellProps: () => getCommonCellProps(minWidth),
        setCellHeaderProps: () => getCommonHeaderProps(minWidth),
    },
});

// Create a date column with formatting
export const createDateColumn = (name, label, tableData, minWidth = "fit-content") => ({
    name: name,
    label: label,
    options: {
        customBodyRenderLite: (dataIndex) => {
            const item = tableData[dataIndex];
            if (item[name] !== "-") {
                let date = new Date(item[name]);
                return date.toLocaleDateString("en-US");
            }
            return "-";
        },
        setCellProps: () => getCommonCellProps(minWidth),
        setCellHeaderProps: () => getCommonHeaderProps(minWidth),
    },
});

// Create a basic column with common styling
export const createBasicColumn = (name, label, minWidth = "fit-content") => ({
    name: name,
    label: label,
    options: {
        setCellProps: () => getCommonCellProps(minWidth),
        setCellHeaderProps: () => getCommonHeaderProps(minWidth),
    },
});

// Create a right-aligned numeric column (cell data only, header uses CSS)
export const createNumericColumn = (name, label, minWidth = "fit-content") => ({
    name: name,
    label: label,
    options: {
        setCellProps: () => ({
            style: {
                minWidth: minWidth,

                paddingRight: "17px",
                textAlign: "right",
            },
        }),
        setCellHeaderProps: () => getCommonHeaderProps(minWidth),
    },
});

// Create a link column (for Hubspot, Netsuite, etc)
export const createLinkColumn = (name, label, linkField = `${name}_customer_link`) => ({
    name: name,
    label: label,
    options: {
        customBodyRenderLite: (dataIndex, tableData) => {
            const item = tableData[dataIndex];
            const id = item[`${name}_id`]?.toString() || "-";
            const link = item[linkField] || "#";

            return (
                <a rel="noreferrer" href={link} target="_blank">
                    {id}
                </a>
            );
        },
        setCellProps: () => getCommonCellProps(),
        setCellHeaderProps: () => getCommonHeaderProps(),
    },
});

// Create a currency column that sorts correctly numerically
export const createNumericCurrencyColumn = (name, label, minWidth = "150px", tableData) => ({
    name: name,
    label: label,
    options: {
        setCellProps: () => ({
            style: {
                minWidth: minWidth,

                paddingRight: "20px",
                textAlign: "right",
            },
        }),
        setCellHeaderProps: () => ({
            style: {
                minWidth: minWidth,
                fontWeight: "bold",
                textAlign: "right",
                paddingRight: "20px",
            },
        }),
        // Custom sort for currency values
        sortCompare: (order) => {
            return (obj1, obj2) => {
                // Extract numeric values from currency strings
                let val1 = obj1.data;
                let val2 = obj2.data;

                // Convert currency strings to numbers
                if (typeof val1 === "string") {
                    val1 = parseFloat(val1.replace(/[^0-9.-]+/g, ""));
                }
                if (typeof val2 === "string") {
                    val2 = parseFloat(val2.replace(/[^0-9.-]+/g, ""));
                }

                // Handle undefined, null, or NaN values
                if (val1 === undefined || val1 === null || isNaN(val1)) val1 = 0;
                if (val2 === undefined || val2 === null || isNaN(val2)) val2 = 0;

                return (order === "asc" ? 1 : -1) * (val1 - val2);
            };
        },
    },
});

// Add this new helper function
export const createCurrencyColumn = (name, label, minWidth = "150px") => ({
    ...createBasicColumn(name, label, minWidth),
    name: name,
    label: label,
    options: {
        ...createBasicColumn(name, label, minWidth).options,
        setCellProps: () => ({
            style: {
                minWidth: minWidth,

                paddingRight: "35px",
                textAlign: "right",
            },
        }),
        setCellHeaderProps: () => ({
            style: {
                minWidth: minWidth,
                width: "100%",
                fontWeight: "bold",
                textAlign: "right",
                paddingRight: "20px",
                display: "flex",
                justifyContent: "flex-end",
            },
        }),
    },
});
